/**
 * encoding.ts
 * UTF-8テキストをGB独自1バイトコードへ変換する。
 * textEngine.inoのparseJPto1byte/wrapdataTo7bitと同じロジック。
 */

// --- 1バイトコード → UTF-8文字列 の逆引きマップ (codeToUTF8相当) ---
// 後段でも使うため先に定義する。

const CODE_TO_UTF8: Record<number, string> = {};
(function buildReverseMap() {
    // 0x20-0x7E : ASCII (code = char - 0x20), ただし特殊文字を除く
    for (let ch = 0x20; ch <= 0x7e; ch++) {
        const code = ch - 0x20;
        if (code === 0)
            CODE_TO_UTF8[code] = " "; // 0 = スペース
        else CODE_TO_UTF8[code] = String.fromCharCode(ch);
    }
    // 特殊上書き
    CODE_TO_UTF8[0] = " "; // スペース
    CODE_TO_UTF8[211] = ")";
    CODE_TO_UTF8[214] = " "; // 全角/半角スペース
    CODE_TO_UTF8[215] = "+";
    CODE_TO_UTF8[216] = "*";
    CODE_TO_UTF8[212] = "…";
    CODE_TO_UTF8[213] = "■";

    // JP_MAP: code 95..210
    const JP_MAP = [
        "、",
        "。",
        "「",
        "」",
        "ぁ",
        "あ",
        "ぃ",
        "い",
        "ぅ",
        "う",
        "ぇ",
        "え",
        "ぉ",
        "お",
        "か",
        "き",
        "く",
        "け",
        "こ",
        "さ",
        "し",
        "す",
        "せ",
        "そ",
        "た",
        "ち",
        "っ",
        "つ",
        "て",
        "と",
        "な",
        "に",
        "ぬ",
        "ね",
        "の",
        "は",
        "ひ",
        "ふ",
        "へ",
        "ほ",
        "ま",
        "み",
        "む",
        "め",
        "も",
        "ゃ",
        "や",
        "ゅ",
        "ゆ",
        "ょ",
        "よ",
        "ら",
        "り",
        "る",
        "れ",
        "ろ",
        "わ",
        "を",
        "ん",
        "ァ",
        "ア",
        "ィ",
        "イ",
        "ゥ",
        "ウ",
        "ェ",
        "エ",
        "ォ",
        "オ",
        "カ",
        "キ",
        "ク",
        "ケ",
        "コ",
        "サ",
        "シ",
        "ス",
        "セ",
        "ソ",
        "タ",
        "チ",
        "ッ",
        "ツ",
        "テ",
        "ト",
        "ナ",
        "ニ",
        "ヌ",
        "ネ",
        "ノ",
        "ハ",
        "ヒ",
        "フ",
        "ヘ",
        "ホ",
        "マ",
        "ミ",
        "ム",
        "メ",
        "モ",
        "ャ",
        "ヤ",
        "ュ",
        "ユ",
        "ョ",
        "ヨ",
        "ラ",
        "リ",
        "ル",
        "レ",
        "ロ",
        "ワ",
        "ヲ",
        "ン",
        "・",
        "ー",
    ];
    for (let i = 0; i < JP_MAP.length; i++) {
        CODE_TO_UTF8[95 + i] = JP_MAP[i];
    }
})();

export { CODE_TO_UTF8 };

// --- UTF-8 → 1バイトコード 変換 (parseJPto1byte相当) ---

interface RangeResult {
    code: number;
    dakuten: number;
    skip: number;
}

function processRange(b1: number, b2: number, b3: number): RangeResult | null {
    let dFlg = 0;
    let code = 0;
    if (b1 === 0xe3) {
        if (b2 === 0x81) {
            if (b3 >= 0x81 && b3 <= 0x8a) {
                code = 99 + (b3 - 0x81);
            } else if (b3 >= 0x8b && b3 <= 0xa2) {
                dFlg = (b3 - 0x8b) % 2;
                code = 109 + Math.floor((b3 - 0x8b) / 2);
            } else if (b3 >= 0xa4 && b3 <= 0xa5) {
                dFlg = (b3 - 0xa4) % 2;
                code = 122;
            } else if (b3 >= 0xa6 && b3 <= 0xa9) {
                dFlg = (b3 - 0xa6) % 2;
                code = 123 + Math.floor((b3 - 0xa6) / 2);
            } else if (b3 >= 0xaa && b3 <= 0xae) {
                code = 125 + (b3 - 0xaa);
            } else if (b3 >= 0xaf && b3 <= 0xbd) {
                dFlg = (b3 - 0xaf) % 3;
                if (dFlg === 2) dFlg = 2;
                else if (dFlg === 1) dFlg = 1;
                code = 130 + Math.floor((b3 - 0xaf) / 3);
            } else if (b3 >= 0xbe && b3 <= 0xbf) {
                code = 135 + (b3 - 0xbe);
            } else {
                return null;
            }
        } else if (b2 === 0x82) {
            if (b3 >= 0x80 && b3 <= 0x82) {
                code = 137 + (b3 - 0x80);
            } else if (b3 >= 0x83 && b3 <= 0x88) {
                code = 140 + (b3 - 0x83);
            } else if (b3 >= 0x89 && b3 <= 0x8d) {
                code = 146 + (b3 - 0x89);
            } else if (b3 === 0x99) {
                // 結合濁点
                return { code: 0, dakuten: 1, skip: 3 };
            } else if (b3 === 0x9a) {
                // 結合半濁点
                return { code: 0, dakuten: 2, skip: 3 };
            } else if (b3 >= 0xa1 && b3 <= 0xaa) {
                code = 154 + (b3 - 0xa1);
            } else if (b3 >= 0xab && b3 <= 0xbf) {
                dFlg = (b3 - 0xab) % 2;
                code = 164 + Math.floor((b3 - 0xab) / 2);
            } else {
                return null;
            }
        } else if (b2 === 0x83) {
            if (b3 >= 0x81 && b3 <= 0x82) {
                dFlg = (b3 - 0x81) % 2;
                code = 175;
            } else if (b3 >= 0x84 && b3 <= 0x85) {
                dFlg = (b3 - 0x84) % 2;
                code = 177;
            } else if (b3 >= 0x86 && b3 <= 0x89) {
                dFlg = (b3 - 0x86) % 2;
                code = 178 + Math.floor((b3 - 0x86) / 2);
            } else if (b3 >= 0x8a && b3 <= 0x8e) {
                code = 180 + (b3 - 0x8a);
            } else if (b3 >= 0x8f && b3 <= 0x9d) {
                dFlg = (b3 - 0x8f) % 3;
                if (dFlg === 2) dFlg = 2;
                else if (dFlg === 1) dFlg = 1;
                code = 185 + Math.floor((b3 - 0x8f) / 3);
            } else if (b3 >= 0x9e && b3 <= 0xa2) {
                code = 190 + (b3 - 0x9e);
            } else if (b3 >= 0xa3 && b3 <= 0xa8) {
                code = 195 + (b3 - 0xa3);
            } else if (b3 >= 0xa9 && b3 <= 0xad) {
                code = 201 + (b3 - 0xa9);
            } else {
                return null;
            }
        } else {
            return null;
        }
        return { code, dakuten: dFlg, skip: 3 };
    }
    return null;
}

interface SpecialChar {
    b1: number;
    b2: number;
    b3: number;
    output: number;
    flags: number;
}
const SPECIAL_CHARS: SpecialChar[] = [
    { b1: 0xe3, b2: 0x80, b3: 0x81, output: 95, flags: 0 }, // 、
    { b1: 0xe3, b2: 0x80, b3: 0x82, output: 96, flags: 0 }, // 。
    { b1: 0xe3, b2: 0x80, b3: 0x8c, output: 97, flags: 0 }, // 「
    { b1: 0xe3, b2: 0x80, b3: 0x8d, output: 98, flags: 0 }, // 」
    { b1: 0xe3, b2: 0x80, b3: 0x9c, output: 94, flags: 0 }, // 〜
    { b1: 0xe3, b2: 0x80, b3: 0x80, output: 214, flags: 0 }, // 全角スペース
    { b1: 0xe3, b2: 0x81, b3: 0xa3, output: 121, flags: 0 }, // っ
    { b1: 0xe3, b2: 0x82, b3: 0x8f, output: 151, flags: 0 }, // わ
    { b1: 0xe3, b2: 0x82, b3: 0x92, output: 152, flags: 0 }, // を
    { b1: 0xe3, b2: 0x82, b3: 0x93, output: 153, flags: 0 }, // ん
    { b1: 0xe3, b2: 0x83, b3: 0x80, output: 174, flags: 1 }, // ダ
    { b1: 0xe3, b2: 0x83, b3: 0x83, output: 176, flags: 0 }, // ッ
    { b1: 0xe3, b2: 0x83, b3: 0xaf, output: 206, flags: 0 }, // ワ
    { b1: 0xe3, b2: 0x83, b3: 0xb2, output: 207, flags: 0 }, // ヲ
    { b1: 0xe3, b2: 0x83, b3: 0xb3, output: 208, flags: 0 }, // ン
    { b1: 0xe3, b2: 0x83, b3: 0xbb, output: 209, flags: 0 }, // ・
    { b1: 0xe3, b2: 0x83, b3: 0xbc, output: 210, flags: 0 }, // ー
    { b1: 0xe2, b2: 0x80, b3: 0xa6, output: 212, flags: 0 }, // …
    { b1: 0xe2, b2: 0x96, b3: 0xa0, output: 213, flags: 0 }, // ■
    { b1: 0xef, b2: 0xbc, b3: 0x81, output: 1, flags: 0 }, // ！
    { b1: 0xef, b2: 0xbc, b3: 0x9f, output: 31, flags: 0 }, // ？
];

/**
 * UTF-8文字列をGB独自1バイトコード配列に変換する (parseJPto1byte相当)
 */
export function encodeText(utf8: string): Uint8Array {
    const bytes = new TextEncoder().encode(utf8);
    const result: number[] = [];
    let i = 0;
    while (i < bytes.length) {
        const b = bytes[i];
        if ((b & 0x80) === 0) {
            // 1バイトASCII
            if (b >= 0x20 && b <= 0x7e) {
                if (b === 0x29)
                    result.push(211); // )
                else if (b === 0x2b)
                    result.push(215); // +
                else if (b === 0x2a)
                    result.push(216); // *
                else if (b === 0x20)
                    result.push(214); // スペース
                else result.push(b - 0x20);
            } else {
                result.push(b);
            }
            i++;
        } else if ((b & 0xe0) === 0xc0) {
            // 2バイト文字 (未サポート範囲はスキップ)
            i += 2;
        } else if ((b & 0xf0) === 0xe0) {
            // 3バイト文字
            const b2 = bytes[i + 1] ?? 0;
            const b3 = bytes[i + 2] ?? 0;
            // 特殊文字テーブルを先に確認
            const special = SPECIAL_CHARS.find(
                s => s.b1 === b && s.b2 === b2 && s.b3 === b3,
            );
            if (special) {
                result.push(special.output);
                if (special.flags === 1)
                    result.push(254); // 濁点
                else if (special.flags === 2) result.push(255); // 半濁点
                i += 3;
                continue;
            }
            const rangeResult = processRange(b, b2, b3);
            if (rangeResult) {
                if (rangeResult.code !== 0) result.push(rangeResult.code);
                if (rangeResult.dakuten === 1) result.push(254);
                else if (rangeResult.dakuten === 2) result.push(255);
            }
            i += 3;
        } else {
            i++;
        }
    }
    return new Uint8Array(result);
}

/**
 * 1バイトコード配列をwrapdataTo7bit形式にエンコード。
 * - 0x80以上のバイト: 0x0B プレフィックス + (byte ^ 0x80) (ただし0x80→0x7D, 0x82→0x7C)
 * - 0x80未満: そのまま
 * 終端の0x00は含まない（呼び出し側が \0 を末尾に置く）。
 */
export function encode7bit(bytes: Uint8Array): Uint8Array {
    const result: number[] = [];
    for (let i = 0; i < bytes.length; i++) {
        const byte = bytes[i];
        if (byte & 0x80) {
            result.push(0x0b); // 8bit文字ヘッダ
            if (byte === 0x80) result.push(0x7d);
            else if (byte === 0x82) result.push(0x7c);
            else result.push(byte ^ 0x80);
        } else {
            result.push(byte);
        }
    }
    return new Uint8Array(result);
}

/**
 * バイナリデータをGDAT用7bitエンコードに変換 (ESP側wrapTileDataTo7bit相当)
 * 各バイトを2バイトにエンコード:
 *   0x00 → 0x40 0x01
 *   0x80 → 0x44 0x01
 *   other → (0x42 | (byte >> 7)), (byte & 0x7F)
 */
export function wrapDataTo7bit(data: Uint8Array): Uint8Array {
    const result = new Uint8Array(data.length * 2);
    let pos = 0;
    for (let i = 0; i < data.length; i++) {
        const byte = data[i];
        if (byte === 0x00) {
            result[pos++] = 0x40;
            result[pos++] = 0x01;
        } else if (byte === 0x80) {
            result[pos++] = 0x44;
            result[pos++] = 0x01;
        } else {
            result[pos++] = 0x42 | (byte >> 7);
            result[pos++] = byte & 0x7f;
        }
    }
    return result.slice(0, pos);
}

/**
 * wrapdataTo7bitのデコード (GB側のwrapdataTo8bit相当)
 * BUSY/WAIT/TENDなどASCIIパケットには適用不要。
 * テキストチャンク T[API][SUCCESS][signal][data...] のdata部分に使う。
 */
export function decode7bit(bytes: Uint8Array): Uint8Array {
    const result: number[] = [];
    let i = 0;
    while (i < bytes.length) {
        if (bytes[i] === 0x0b) {
            i++;
            if (i >= bytes.length) break;
            const v = bytes[i];
            if (v === 0x7d) result.push(0x80);
            else if (v === 0x7c) result.push(0x82);
            else result.push(v | 0x80);
        } else {
            result.push(bytes[i]);
        }
        i++;
    }
    return new Uint8Array(result);
}
